import {
  ActionIcon,
  Box,
  Button,
  Center,
  createStyles,
  Divider,
  Flex,
  Menu,
  Modal,
  Stack,
  Text,
  useMantineTheme,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { ITrackedEquipment, ITrackedEquipmentRepair } from '../../../../models';
import {
  DialogModal,
  InfoButton,
  TrackedEquipmentDetailSection,
  TrackedEquipmentPatientSection,
  TrackedEquipmentRepairDetailSection,
  TrackedEquipmentRepairNoteSection,
} from '../../../../components';
import { useEffect, useState } from 'react';
import { useDeleteTrackedEquipmentRepair, useIsAdmin, useIsTechnician } from '../../../../hooks';
import { ArrowLeft, Dots, Hammer, MapPin, Trash } from 'tabler-icons-react';

const useStyles = createStyles((theme) => ({
  notesCard: {
    border: `1px solid ${theme.colors.gray[4]}`,
    borderRadius: theme.radius.sm,
    paddingLeft: theme.spacing.sm,
    paddingRight: theme.spacing.sm,
    paddingTop: 3,
    width: '100%',
    height: '125px',
  },
  infoBox: {
    color: `${theme.colors.gray[6]}`,
    border: `1px solid ${theme.colors.gray[6]}`,
    borderRadius: theme.radius.lg,
    width: 'fit-content',
    paddingLeft: '10px',
    paddingRight: '10px',
  },
  groupLabel: {
    color: theme.colors.gray[5],
    fontSize: theme.fontSizes.sm,
    whiteSpace: 'pre',
  },
  fieldLabel: {
    color: theme.colors.gray[5],
    fontSize: theme.fontSizes.xs,
    whiteSpace: 'pre',
  },
  editLabel: {
    fontSize: theme.fontSizes.xs,
    fontWeight: 500,
    whiteSpace: 'pre',
  },
  detail: {
    fontSize: theme.fontSizes.md,
    paddingLeft: 10,
  },
  fieldGroup: {
    width: '48%',
  },
  clickableIcon: {
    color: `${theme.colors.blue[6]}`,
    padding: '0 !important',
  },
  buttonText: {
    [`@media (max-width: ${theme.breakpoints.sm})`]: {
      fontSize: 12,
    },
  },
}));

type TrackedEquipmentRepairDetailModalProps = {
  trackedEquipment: ITrackedEquipment;
  repair: ITrackedEquipmentRepair;
  opened: boolean;
  onClose: () => void;
  onComplete: () => void;
  onDelete: () => void;
  onAddRepairLocation: () => void;
};

const TrackedEquipmentRepairDetailModal = (props: TrackedEquipmentRepairDetailModalProps) => {
  const { classes } = useStyles();
  const theme = useMantineTheme();
  const isSmallMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`);

  const isAdmin = useIsAdmin().data?.isAdmin;
  const isTechnician = useIsTechnician().data?.isTechnician;

  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);

  const [isError, setIsError] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');

  const deleteTrackedEquipmentRepair = useDeleteTrackedEquipmentRepair();

  const handleDelete = () => {
    deleteTrackedEquipmentRepair.mutate({
      trackedEquipmentRepairId: props.repair.trackedEquipmentRepairId,
    });
  };

  useEffect(() => {
    if (deleteTrackedEquipmentRepair.isSuccess) {
      props.onDelete();
    }
    if (deleteTrackedEquipmentRepair.isError) {
      setIsError(true);
      setMessage('Something went wrong, Repair not Deleted');
    }
  }, [deleteTrackedEquipmentRepair.status]);

  return (
    <Modal
      title={<Text color={isError ? 'red' : 'green'}>{message}</Text>}
      opened={props.opened}
      size={isSmallMobile ? 'xs' : 'md'}
      onClose={() => {
        props.onClose();
      }}
    >
      <Stack spacing={0} style={{ position: 'relative' }}>
        <Center>
          <Text size="xl" weight={500} align="center">
            Equipment Repair Record
          </Text>
          <Flex
            direction={isSmallMobile ? 'column' : 'row'}
            align="center"
            style={{ position: 'absolute', right: -5 }}
          >
            <InfoButton
              userId={props.repair?.lastUpdatedUserId ?? ''}
              date={props.repair?.lastUpdatedDateTime ?? new Date()}
              ml={5}
            />
            {(isAdmin || isTechnician) && (
              <Menu>
                <Menu.Target>
                  <ActionIcon>
                    <Dots />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    icon={<MapPin color={`${theme.colors.blue[6]}`} />}
                    onClick={() => {
                      props.onAddRepairLocation();
                    }}
                  >
                    Add Repair Location
                  </Menu.Item>
                  <Menu.Item
                    icon={<Trash color={`${theme.colors.red[6]}`} />}
                    onClick={() => {
                      setDeleteModalOpen(true);
                    }}
                  >
                    Delete Repair Record
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            )}
          </Flex>
        </Center>
        <TrackedEquipmentDetailSection trackedEquipment={props.trackedEquipment} readOnly />

        <TrackedEquipmentRepairDetailSection
          repair={props.repair}
          readOnly={props.trackedEquipment.retired || props.repair.returnedDateTime !== undefined}
          onEdited={() => {
            setIsError(false);
            setMessage('Repair Details Successfully Updated!');
          }}
          onError={() => {
            setIsError(true);
            setMessage('Something went wrong, Repair Details not Updated');
          }}
        />
        <Box mb={5}>
          <TrackedEquipmentRepairNoteSection
            trackedEquipmentRepair={props.repair}
            readOnly={props.trackedEquipment.retired || props.repair.returnedDateTime !== undefined}
            onEdited={() => {
              setIsError(false);
              setMessage('Equipment Notes Successfully Updated!');
            }}
            onError={() => {
              setIsError(true);
              setMessage('Something went wrong, Equipment Notes not Updated');
            }}
          />
        </Box>
        {!props.repair.returnedDateTime && (
          <Box style={{ width: '100%' }} mt={`calc(${theme.spacing.xs} / 2)`}>
            <Stack spacing={theme.spacing.xs}>
              <Button
                variant="outline"
                onClick={() => {
                  props.onComplete();
                }}
              >
                <Hammer style={{ marginRight: 5 }} />
                Complete Repair
              </Button>
              <Divider />
            </Stack>
          </Box>
        )}
        <Button
          variant="outline"
          mt="xs"
          onClick={() => {
            props.onClose();
          }}
        >
          <ArrowLeft style={{ marginRight: 5 }} />
          Return to Repair History
        </Button>
      </Stack>
      <DialogModal
        isOpen={deleteModalOpen}
        isNegative
        titleText="Delete Repair Record?"
        body={
          <Text align="center" color="dimmed" size="lg" mb="xs">
            Are you sure you want to delete this Equipment?
          </Text>
        }
        confirmText="Delete Repair"
        onConfirm={() => {
          setDeleteModalOpen(false);
          handleDelete();
        }}
        onClose={() => {
          setDeleteModalOpen(false);
        }}
      />
    </Modal>
  );
};

export default TrackedEquipmentRepairDetailModal;
